import axios, { AxiosError, type AxiosInstance } from 'axios';
import { useAuthStore } from '@/stores/auth.store';

export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
  timeout: 30_000,
});

apiClient.interceptors.request.use((config) => {
  const { accessToken, activeTenantId } = useAuthStore.getState();
  config.headers = config.headers ?? {};
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (activeTenantId && !config.headers['X-Tenant-Id']) {
    config.headers['X-Tenant-Id'] = activeTenantId;
  }
  return config;
});

let refreshing: Promise<string | null> | null = null;

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      !original.url?.includes('/auth/')
    ) {
      original._retry = true;
      refreshing ??= doRefresh();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
        return apiClient.request(original);
      }
      useAuthStore.getState().clear();
    }
    return Promise.reject(error);
  },
);

async function doRefresh(): Promise<string | null> {
  try {
    const res = await axios.post(
      '/api/auth/refresh',
      {},
      { withCredentials: true, timeout: 10_000 },
    );
    const token = (res.data as { accessToken?: string }).accessToken;
    if (token) {
      useAuthStore.getState().setAccessToken(token);
      return token;
    }
    return null;
  } catch {
    return null;
  }
}
